<template>
    <x-container fluid pa-0>
        <x-form ref="firmOrderForm" @submit.prevent="submitFirmOrderForm">
            <x-layout class="mb-3">
                <x-flex>
                    <h3>Confirm firm order</h3>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs6 class="left">
                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>Client details</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-container pa-0>
                                <x-layout mb-3>
                                    <x-flex>
                                        <entity-summary :submission="submission" />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex>
                                        <x-radio-group
                                            v-model="clientCodeType"
                                            class="required"
                                            :row="true"
                                            :rules="[
                                                v => ruleRequired(v, 'Placing broker entity')
                                            ]"
                                        >
                                            <template v-slot:label>
                                                Placing broker entity
                                            </template>

                                            <x-radio
                                                v-show="(clientCodeSourceHasClientCode && clientCodeSource.hibl_client_code) || !clientCodeSourceHasClientCode"
                                                label="HIBL"
                                                value="hibl"
                                            />

                                            <x-radio
                                                v-show="(clientCodeSourceHasClientCode && clientCodeSource.lux_client_code) || !clientCodeSourceHasClientCode"
                                                label="LUX"
                                                value="lux"
                                            />
                                        </x-radio-group>
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-card-text>
                    </x-card>

                    <x-card v-if="isCaptiveReinsurance" class="panel mb-5">
                        <x-card-title>
                            <h3>BNPP information</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-container pa-0>
                                <x-layout mb-3>
                                    <x-flex>
                                        <x-select
                                            v-model="bookedLocationId"
                                            :items="sortedCities"
                                            class="required"
                                            label="Booking location"
                                            placeholder="Booking location"
                                            item-value="id"
                                            item-text="name"
                                            :rules="[
                                                v => ruleRequired(v, 'Booking location')
                                            ]"
                                            append-icon="keyboard_arrow_down"
                                            :menu-props="{ offsetY: true }"
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout mb-3>
                                    <x-flex>
                                        <x-text-field
                                            v-model="insuredTransaction"
                                            class="required"
                                            label="Insured transaction"
                                            placeholder=" "
                                            :rules="[
                                                v => ruleRequired(v, 'Insured transaction')
                                            ]"
                                            maxlength="250"
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout mb-3>
                                    <x-flex>
                                        <x-text-field
                                            v-model="specialConditions"
                                            class="required"
                                            label="Special conditions"
                                            placeholder=" "
                                            :rules="[
                                                v => ruleRequired(v, 'Special conditions')
                                            ]"
                                            maxlength="250"
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex>
                                        <x-textarea
                                            v-model="obligorAddress"
                                            class="required"
                                            label="Obligor address"
                                            placeholder=" "
                                            :rules="[
                                                v => ruleRequired(v, 'Obligor address')
                                            ]"
                                            maxlength="500"
                                        />
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-card-text>
                    </x-card>

                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>Dates</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-container pa-0>
                                <x-layout mb-4>
                                    <x-flex>
                                        <data-list v-model="datesDataList" />
                                    </x-flex>
                                </x-layout>

                                <x-layout mb-3>
                                    <x-flex>
                                        <x-menu
                                            v-model="startDateInput"
                                            :close-on-content-click="false"
                                            lazy
                                            transition="scale-transition"
                                            offset-y
                                            max-width="300"
                                            full-width
                                        >
                                            <template v-slot:activator="{ on }">
                                                <x-text-field
                                                    data-test="start_date"
                                                    v-model="startDate"
                                                    class="required"
                                                    :label="getPropertyLabel('start_date')"
                                                    append-icon="event"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>

                                            <x-date-picker v-model="startDate" @input="startDateInput = false" :max="endDate" />
                                        </x-menu>
                                    </x-flex>
                                </x-layout>

                                <x-layout mb-3>
                                    <x-flex>
                                        <x-menu
                                            v-model="endDateInput"
                                            :close-on-content-click="false"
                                            lazy
                                            transition="scale-transition"
                                            offset-y
                                            max-width="300"
                                            full-width
                                        >
                                            <template v-slot:activator="{ on }">
                                                <x-text-field
                                                    v-model="endDate"
                                                    class="required"
                                                    :label="getPropertyLabel('end_date')"
                                                    append-icon="event"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>

                                            <x-date-picker v-model="endDate" @input="endDateInput = false" :min="startDate" />
                                        </x-menu>
                                    </x-flex>
                                </x-layout>

                                <x-layout v-if="!this.validDate" mb-3>
                                    <x-flex>
                                        <x-alert
                                            :value="true"
                                            color="error"
                                            icon="warning"
                                            outline
                                        >
                                            {{ this.dateError }}
                                        </x-alert>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex>
                                        <x-text-field
                                            v-model="policyUmr"
                                            label="Policy UMR"
                                            placeholder=" "
                                            :rules="[
                                                v => ruleRequired(v, 'Policy UMR')
                                            ]"
                                            required
                                            readonly
                                        />
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-card-text>
                    </x-card>

                    <x-card v-if="submissionHasWaitingPeriodInterest" class="panel mb-5">
                        <x-card-title>
                            <h3>Waiting Period Interest</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-text-field
                                v-model="waitingPeriodInterestRate"
                                class="value-percent-percentage required input-text-right"
                                label="Waiting Period Interest Rate"
                                suffix="%"
                                :rules="[
                                    v => ruleRequired(v, 'Percentage'),
                                    v => ruleLessThanOrEqualTo(v, 100, 'Percentage'),
                                    v => ruleGreaterThanOrEqualTo(v, -100, 'Percentage')
                                ]"
                                @keypress="onlyForCurrency"
                            />
                        </x-card-text>
                    </x-card>
                </x-flex>

                <x-flex xs6 class="right">
                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>MRC details</h3>
                        </x-card-title>

                        <x-card-text>
                            <m-r-c-v-3-submission-fields :submission="submission" :submissionData="submissionData"/>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12 class="right">
                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>Subscription agreement</h3>
                        </x-card-title>

                        <x-card-text>
                            <template v-if="isCaptiveReinsurance && currentStep === 2">
                                <h5 class="mb-4">Captive firm order</h5>

                                <x-data-table
                                    class="no-row-hover mb-4"
                                    :headers="subscriptionAgreementHeaders"
                                    :items="[captiveFormattedOffer]"
                                    hide-actions
                                >
                                    <template v-slot:no-data>
                                        <p class="error-message text-xs-center">No offers found</p>
                                    </template>

                                    <template v-slot:items="offer">
                                        <tr>
                                            <td><strong>{{ offer.item.captive_insurer }}</strong></td>

                                            <td>
                                                <p v-if="getInsurerSecurities(offer.item).length === 0">No required</p>

                                                <p v-else-if="getInsurerSecurities(offer.item).length === 1">
                                                    {{ getInsurerSecurities(offer.item)[0].name }}
                                                </p>

                                                <x-select
                                                    v-else-if="getInsurerSecurities(offer.item).length > 1"
                                                    v-model="offer.item.default_security_id"
                                                    :items="getSecuritiesFormatted(getInsurerSecurities(offer.item))"
                                                    class="required"
                                                    label="Securities"
                                                    append-icon="keyboard_arrow_down"
                                                    :rules="[v => ruleRequired(v, 'Security', 'Select a security for this insurer')]"
                                                    :menu-props="{
                                                        offsetY: true,
                                                        contentClass: 'input-fixed-width-menu',
                                                    }"
                                                    @change="value => setSecurity(offer.item, value)"
                                                >
                                                    <template slot="selection" slot-scope="{item, index}">
                                                        <span :title="item.text">{{ item.text }}</span>
                                                    </template>
                                                </x-select>
                                            </td>

                                            <td>
                                                {{
                                                    offer.item.written_line_amount | currency(submission.currency.code)
                                                }}
                                            </td>

                                            <td class="text-xs-right">
                                                N/A

                                                <span v-if="showHorizontalPriceChange(0)">
                                                    <x-icon size="18">arrow_upward</x-icon> {{ pricing.horizontalPrice }} BPS
                                                </span>
                                            </td>

                                            <td class="text-xs-center"
                                                :class="{ 'no-padding': !offer.item.subjectivities.length }"
                                            >
                                                <warning-items
                                                    v-if="offer.item.subjectivities.length > 0"
                                                    v-model="offer.item.subjectivities"
                                                    item-text="name"
                                                    item-width="200"
                                                />
                                            </td>
                                        </tr>
                                    </template>
                                </x-data-table>

                                <h5 class="mb-4">Reinsurance firm order</h5>
                            </template>

                            <x-data-table
                                class="no-row-hover"
                                :headers="headers"
                                :items="insurerFormattedData"
                                item-key="row_key"
                                hide-actions
                            >
                                <template v-slot:no-data>
                                    <p class="error-message text-xs-center">No offers found</p>
                                </template>

                                <template v-slot:items="offer">
                                    <td>
                                        <p class="mb-1"><strong>{{ offer.item.insurer }}</strong></p>

                                        <div>
                                            <p v-if="getInsurerSecurities(offer.item).length === 0">No required</p>

                                            <!-- Securities and line splits are managed on the firm
                                                 order builder (or the manual firm order step); this
                                                 screen only confirms the result. -->
                                            <p v-else>{{ rowSecurityName(offer.item) }}</p>
                                        </div>
                                    </td>

                                    <td>
                                        <p class="mb-0">{{ offer.item.written_line_percent | percent }}</p>
                                        <p class="mb-0">{{ offer.item.written_line_amount | currency(submission.currency.code) }}</p>
                                    </td>

                                    <td v-if="submissionHasWaitingPeriodInterest">
                                        <x-tooltip top>
                                            <template v-slot:activator="{ on }">
                                                <div v-on="on">
                                                    {{ calculateMaximumInsuredExposure(offer.item.waiting_period, offer.item.written_line_amount) | currency(submission.currency.code) }}
                                                </div>
                                            </template>

                                            <p>
                                                Written line: {{ offer.item.written_line_amount | currency(submission.currency.code) }}
                                            </p>

                                            <p>
                                                Interest rate: {{ waitingPeriodInterestRate | percent }}
                                            </p>

                                            <p>
                                                Waiting period interest: {{ calculateWaitingPeriodInterest(offer.item.written_line_amount, offer.item.waiting_period) | currency(submission.currency.code) }}
                                            </p>

                                            <p>
                                                Total: {{ calculateMaximumInsuredExposure(offer.item.waiting_period, offer.item.written_line_amount) | currency(submission.currency.code) }}
                                            </p>
                                        </x-tooltip>
                                    </td>

                                    <td>
                                        <x-select
                                            :value="offer.item.line.insurer_role"
                                            :items="insurerRoleOptions"
                                            placeholder="Optional"
                                            clearable
                                            hide-details
                                            append-icon="keyboard_arrow_down"
                                            :menu-props="{
                                                offsetY: true,
                                                contentClass: 'input-fixed-width-menu',
                                            }"
                                            @change="value => setLineInsurerRole(offer.item, value)"
                                        />
                                    </td>

                                    <td class="text-xs-right" v-if="isRateOffer(offer.item.type) || isPercentageOfMarginOffer(offer.item.type)">
                                        {{ offer.item.price }} BPS

                                        <span v-if="showHorizontalPriceChange(offer.item.price)">
                                             <x-icon size="18">arrow_upward</x-icon> {{ pricing.horizontalPrice }} BPS
                                         </span>
                                    </td>

                                    <td class="text-xs-right" v-else-if="isFlatFeeOffer(offer.item.type)">
                                        {{ offer.item.flat_fee | currency(submission.currency.code) }}
                                    </td>

                                    <td class="text-xs-center" :class="{ 'no-padding': !offer.item.subjectivities.length }">
                                        <warning-items
                                            v-if="offer.item.subjectivities.length > 0 && !isManualFirmOrder"
                                            v-model="offer.item.subjectivities"
                                            item-text="name"
                                            item-width="200"
                                        />
                                    </td>
                                </template>
                            </x-data-table>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs6 class="left">
                    <x-card v-if="selectedOffersSubjectivities.length > 0" class="panel mb-5">
                        <x-card-title>
                            <h3>Subjectivities</h3>
                        </x-card-title>

                        <x-card-text>
                            <h5>Confirm acknowledgement of the following...</h5>

                            <div v-for="offer in selectedOffersWithSubjectivities" :key="offer.id">
                                <p class="mt-3 mb-2"><strong>{{ offer.insurer.name }}</strong></p>

                                <template v-if="isManualFirmOrder && offerLines(offer).length > 1">
                                    <div
                                        v-for="(line, lineIndex) in offerLines(offer)"
                                        :key="`subjectivity-${offer.id}-${lineIndex}`"
                                    >
                                        <p class="mb-0 mt-2">{{ lineSecurityName(offer, line) }}</p>

                                        <x-textarea
                                            v-model="line.manual_subjectivity"
                                            label=" "
                                            placeholder=" "
                                            maxlength="150"
                                        />
                                    </div>
                                </template>

                                <x-textarea
                                    v-else-if="isManualFirmOrder"
                                    v-model="offer.manual_subjectivity"
                                    label=" "
                                    placeholder=" "
                                    maxlength="150"
                                />

                                <check-list
                                    v-else
                                    v-model="offer.subjectivities"
                                    :rules="[allSubjectivitiesSelected]"
                                    :show-title="true"
                                    item-value="name"
                                    item-hide-details
                                    hide-addition
                                />
                            </div>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs6>
                    <x-btn
                        color="tertiary"
                        :disabled="!idle"
                        @click="$emit('firm-order:navigate-to', currentStep - 1)"
                    >
                        Cancel
                    </x-btn>
                </x-flex>

                <x-flex xs6 text-xs-right>
                    <x-btn
                        color="secondary"
                        :loading="saving"
                        :disabled="!formComplete || !idle"
                        @click="saveFirmOrderProgress"
                    >
                        <i class="material-icons mr-2">save</i>

                        {{ saveProgressMessage }}
                    </x-btn>

                    <x-tooltip :disabled="!disableDraftDownload" top>
                        <template v-slot:activator="{ on }">
                            <div v-on="on" class="d-inline-block">
                                <x-btn
                                    v-if="!isManualFirmOrder"
                                    class="ml-2"
                                    color="secondary"
                                    :loading="downloading"
                                    :disabled="disableDraftDownload || !idle"
                                    @click="firmOrderPdf"
                                >
                                    <i class="material-icons mr-2">browser_updated</i>

                                    Download draft Dec
                                </x-btn>
                            </div>
                        </template>

                        <span>Save progress to download draft</span>
                    </x-tooltip>

                    <x-tooltip :disabled="!disableDraftDownload" top>
                        <template v-slot:activator="{ on }">
                            <div v-on="on" class="d-inline-block">
                                <x-btn
                                    v-if="isCaptiveReinsurance && !isManualFirmOrder"
                                    class="ml-2"
                                    color="secondary"
                                    :loading="captiveDownloading"
                                    :disabled="disableDraftDownload || !idle"
                                    @click="captiveFirmOrderPdf"
                                >
                                    <i class="material-icons mr-2">browser_updated</i>

                                    Draft captive PDF
                                </x-btn>
                            </div>
                        </template>

                        <span>Save progress to download captive draft</span>
                    </x-tooltip>

                    <x-btn
                        class="ml-4"
                        color="primary"
                        type="submit"
                        :loading="confirming"
                        :disabled="!confirmButtonEnabled"
                    >
                        Next

                        <i class="material-icons ml-2">trending_flat</i>
                    </x-btn>
                </x-flex>
            </x-layout>

            <x-layout v-if="errors.length > 0" align-center>
                <x-flex xs12>
                    <x-alert
                        :value="true"
                        danger
                        v-for="error in errors"
                        v-bind:key="error"
                    >
                        {{ error }}
                    </x-alert>
                </x-flex>
            </x-layout>
        </x-form>

        <modal
            ref="sofConfirmation"
            title="Statement of Facts"
            actionText="Confirm"
            confirmation
            textLeft
        >
            <statement-of-fact/>
        </modal>

        <modal
            ref="policyRefError"
            title="Error"
        >
            Inception date does not match Policy ref and Declaration UMR. Please re-generate references.
        </modal>
    </x-container>
</template>

<script>
    import DataList from '~/components/Shared/UIComponents/DataList';
    import WarningItems from '~/components/Shared/UIComponents/WarningItems';
    import CheckList from '~/components/Shared/UIComponents/CheckList';
    import EntitySummary from '~/components/Broker/UIComponents/EntitySummary';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import rules from '~/mixins/rules';
    import productConfig from '~/mixins/productConfig';
    import waitingPeriodInterest from '~/mixins/waitingPeriodInterest';
    import maximumInsuredExposure from '~/mixins/maximumInsuredExposure';
    import StatementOfFact from "~/components/Shared/UIComponents/StatementOfFact";
    import premium from '~/mixins/premium';
    import submissionHelpers from "../../../mixins/submissionHelpers";
    import MRCV3SubmissionFields from '~/components/Shared/Partials/MRCV3SubmissionFields';
    import insurerRoleOptions from '~/mixins/insurerRoleOptions';

export default {
    name: 'FirmOrderConfirmation',
    components: {
        DataList,
        WarningItems,
        CheckList,
        EntitySummary,
        StatementOfFact,
        Modal,
        MRCV3SubmissionFields
    },
    mixins: [
        rules,
        productConfig,
        waitingPeriodInterest,
        submissionHelpers,
        insurerRoleOptions,
    ],
    props: {
        submission: Object,
        facilities: Array,
        currentStep: Number,
        pricing: Object,
        horizontalPrice: Number,
        draftFirmOrder: Object,
        cities: Array,
        isManualFirmOrder: {
            type: Boolean,
            default: false
        },
        selectedInsurersUsersList: {
            type: Object,
            default() {
                return {}
            }
        },
        firmOrder: Object,
    },
    data() {
        return {
            availableSecurities: [],
            openConfirmPanelIndex: 0,
            subscriptionAgreementHeaders: [
                {
                    text: 'insurer',
                    value: 'insurer',
                    align: 'left'
                },
                {
                    text: 'Security',
                    value: 'security',
                    align: 'left'
                },
                {
                    text: 'line size',
                    value: 'written_line_amount',
                    align: 'left'
                },
                {
                    text: 'price',
                    value: 'price',
                    align: 'right'
                },
                {
                    text: '',
                    sortable: false,
                    class: 'no-padding',
                    align: 'center'
                }
            ],

            bookedLocationId: this.draftFirmOrder ? this.draftFirmOrder.booked_location_id : null,
            insuredTransaction: this.draftFirmOrder ? this.draftFirmOrder.insured_transaction : null,
            specialConditions: this.draftFirmOrder ? this.draftFirmOrder.special_conditions : null,
            obligorAddress: this.draftFirmOrder ? this.draftFirmOrder.obligor_address : null,

            startDateInput: false,
            endDateInput: false,
            startDate: this.draftFirmOrder ? this.formattedDate(this.draftFirmOrder.inception_date) : null,
            endDate: this.draftFirmOrder ? this.formattedDate(this.draftFirmOrder.expiry_date) : null,
            validDate: true,
            dateError: '',
            saveProgressMessage: 'Save progress',
            disableDraftDownload: true,
            draftFirmOrderId: null,
            policyRef: null,
            policyUmr: null,
            status: 'idle',
            waitingPeriodInterestRate: this.draftFirmOrder ? this.draftFirmOrder.waiting_period_interest_rate : null,
            clientCodeType: null,
            clientCode: null,
            submissionData: {},
            errors: [],
        }
    },
    computed: {
        canEditSecurityLines() {
            return this.isManualFirmOrder || this.submission?.risk_structure === 'standalone_policy';
        },
        confirmButtonEnabled() {
            return this.idle && this.policyRef && this.policyUmr;
        },
        startDateYearForPolicyRef() {
            if (this.startDate) {
                return new Date(this.startDate).getFullYear().toString().slice(-2);
            }
            return null;
        },
        facility() {
            return this.submission.facility;
        },
        entity() {
            if (!this.submission.entity_id) {
                return null;
            }

            const self = this;
            let availableEntities = this.submission.facility?.active_entities;

            if (this.submission.risk_structure === 'standalone_policy') {
                availableEntities = this.submission.client_company.entities;
            }

            return availableEntities.find(function (entity) {
                return entity.id === self.submission.entity_id;
            });
        },
        clientCodeSource() {
            return this.entity || this.submission.client_company;
        },
        clientCodeSourceHasClientCode() {
            return Boolean(this.clientCodeSource.hibl_client_code || this.clientCodeSource.lux_client_code);
        },
        product() {
            return this.submission.product;
        },
        sortedCities() {
            return this.cities.sort((a, b) => a.name.localeCompare(b.name));
        },
        isCaptiveReinsurance() {
            return this.submission.transaction_type === 'captive_reinsurance';
        },
        waitingPeriod: function () {
            return this.selectedOffers
                .map(o => o.waiting_period)
                .reduce(function (a, b) {
                    return Math.max(a, b);
                }, 0);
        },
        offeredOffers: function () {
            return this.submission.offers.filter(offer => offer.status === 'offered');
        },
        selectedOffers: function () {
            return this.offeredOffers.filter(function (offer) {
                return offer.is_selected === true;
            });
        },
        selectedOffersWithSubjectivities() {
            return this.selectedOffers.filter(function (offer) {
                return offer.subjectivities.length > 0;
            });
        },
        selectedOffersSubjectivities() {
            const subjectivities = [];

            this.selectedOffersWithSubjectivities.forEach(function (offer) {
                offer.subjectivities.forEach(function (subjectivity) {
                    subjectivities.push(subjectivity);
                });
            });

            return subjectivities;
        },
        submissionHasWaitingPeriodInterest() {
            return this.submission.facility && waitingPeriodInterest.submissionHasWaitingPeriodInterest(this.submission.facility.waiting_period_interest);
        },
        formattedData() {
            const self = this;
            const rows = [];

            this.selectedOffers.forEach(function (offer) {
                const insurerSecurities = self.getInsurerSecurities(offer);
                const lines = self.offerLines(offer);

                if (insurerSecurities.length === 1 && !offer.security_id) {
                    offer.security_id = insurerSecurities[0].id;
                    lines[0].security_id = insurerSecurities[0].id;
                }

                lines.forEach(function (line, lineIndex) {
                    rows.push({
                        'row_key': `${offer.id}-${lineIndex}`,
                        'offer_id': offer.id,
                        'offer': offer,
                        'line': line,
                        'line_index': lineIndex,
                        'line_count': lines.length,
                        'insurer': !self.isCaptiveReinsurance && offer.insurer !== null ? offer.insurer.name : '',
                        'insurer_company_id': !self.isCaptiveReinsurance && offer.insurer !== null ? offer.insurer.id : '',
                        'captive_insurer': self.isCaptiveReinsurance && offer.captive_insurer !== null ? offer.captive_insurer.name : '',
                        'default_security_id': line.security_id,
                        'securities': offer.securities,
                        'written_line_amount': self.lineWrittenAmount(offer, line),
                        'price': offer.price,
                        'subjectivities': offer.subjectivities,
                        'flat_fee': self.lineFlatFee(offer, line),
                        'percentage_of_margin': offer.percentage_of_margin,
                        'minimum_price': offer.minimum_price,
                        'type': offer.type,
                        'waiting_period': offer.waiting_period,
                        'written_line_percent': lines.length > 1 ? line.written_line_percent : offer.written_line_percent,
                        'manual_estimated_premium': offer.manual_estimated_premium,
                        'insurer_role': line.insurer_role || null,
                    });
                });
            });

            return rows;
        },
        captiveFormattedOffer() {
            return this.formattedData.find(function (offer) {
                return Boolean(offer.captive_insurer);
            });
        },
        insurerFormattedData() {
            return this.formattedData.filter(offer => !offer.captive_insurer);
        },
        firmOrderTenor: function () {
            if (!this.startDate || !this.endDate) {
                return '---'
            }

            return this.$moment(this.endDate).diff(this.$moment(this.startDate), "days");
        },
        validDateRange() {
            const inceptionDate = this.$moment(this.startDate);
            const expiryDate = this.$moment(this.endDate);
            const today = this.$moment();

            // either date is invalid/missing
            if (!inceptionDate.isValid() || !expiryDate.isValid()) {
                this.validDate = false;
                this.dateError = 'Both inception and expiry dates are required.';
                return false
            }

            // inception date same as or after expiry date
            if (inceptionDate.isSame(expiryDate) || inceptionDate.isAfter(expiryDate)) {
                this.validDate = false;
                this.dateError = 'The inception date must be before the expiry date.';
                return false;
            }

            this.validDate = true;
            return true;
        },
        datesDataList() {
            const datesDataList = [
                {
                    text: 'Waiting period',
                    value: `${this.waitingPeriod} days`
                },
            ];

            if (this.submission.total_risk_period) {
                datesDataList.push({
                    text: 'Submission tenor',
                    value: `${this.submission.total_risk_period} days`
                });
            }

            datesDataList.push({
                text: 'Firm order tenor',
                value: `${this.firmOrderTenor} days`
            });

            return datesDataList;
        },
        formComplete() {
            if (this.isCaptiveReinsurance) {
                return this.startDate && this.endDate && this.bookedLocationId && this.insuredTransaction && this.specialConditions && this.obligorAddress && this.clientCodeType;
            }

            return this.startDate && this.endDate && this.clientCodeType && this.validateWaitingPeriodInterestRate;
        },
        validateWaitingPeriodInterestRate() {
            if ((this.waitingPeriodInterestRate && this.submissionHasWaitingPeriodInterest) || !this.submissionHasWaitingPeriodInterest) {
                return true;
            }
            return false;
        },
        idle() {
            return this.status === 'idle';
        },
        saving() {
            return this.status === 'saving';
        },
        confirming() {
            return this.status === 'confirming';
        },
        downloading() {
            return this.status === 'downloading';
        },
        captiveDownloading() {
            return this.status === 'captiveDownloading';
        },
        headers() {
            const headers = [
                {
                    text: 'insurer / security',
                    value: 'insurer',
                    align: 'left'
                },
                {
                    text: 'written line',
                    value: 'written_line_amount',
                    align: 'left'
                },
            ];
            if (this.submissionHasWaitingPeriodInterest) {
                headers.push({
                    text: 'maximum insured exposure',
                    value: 'maximum_insured_exposure',
                    align: 'left'
                });
            }
            headers.push({
                text: 'Insurer role',
                value: 'insurer_role',
                align: 'left'
            });
            headers.push({
                text: 'price',
                value: 'price',
                align: 'right'
            });

            return headers;
        },
        wpiIsIncludedInEstimatedPremium() {
            return this.submission.facility && waitingPeriodInterest.wpiIsIncludedInEstimatedPremium(this.submission.facility.waiting_period_interest);
        },
    },
    watch: {
        selectedOffers: {
            handler(offers) {
                (offers || []).forEach(offer => this.getOfferLines(offer));
            },
            immediate: true
        },
        entity: {
            handler() {
                this.resetEntityClientCodeType();
            },
            immediate: true
        },
        clientCodeSource: {
            handler() {
                this.setClientCode();
            },
            immediate: true
        },
        clientCodeType: {
            handler() {
                this.generateRef();
                this.setClientCode();
            },
            immediate: true
        },
        startDate() {
            if (this.endDate === null && Boolean(this.submission.total_risk_period)) {
                this.endDate = this.$moment(this.startDate).add(this.submission.total_risk_period, 'days').format('YYYY-MM-DD');
            }
            this.changesMade();
        },
        endDate() {
            this.changesMade();
        },
        waitingPeriodInterestRate() {
            this.changesMade();
        },
        startDateYearForPolicyRef() {
            this.generateRef();
        }
    },
    mounted() {
        this.loadInsurerRoleOptions();
        this.loadAvailableSecurities();
        this.clientCodeType = this.submission.client_code_type;

        if (this.submission.manual_firm_order_draft_data) {
            this.syncWithManualFirmOrderDraftData();
        }

        if (this.isConvertedFromManualSubmission) {
            this.startDate ??= this.submission.inception_date;
            this.endDate ??= this.submission.expiry_date;

            this.submission.offers.forEach((offer, index) => {
                const mstInsurer = this.submission.manual_submission_tracker.insurers.find(ins => {
                    return ins.insurer_company_id === offer.insurer_company_id;
                });

                if (mstInsurer) {
                    offer.manual_subjectivity = mstInsurer.subjectivities;
                    this.submission.offers[index] = offer;
                }
            });
        }

        const {
            choice_of_law_id,
            interest,
            taxes_payable_by_insured,
            taxes_payable_by_insurer,
            taxes_payable_by_insurer_and_administered_by_the_insured,
            taxes_payable_and_administered_by_the_insured,
            regulatory_risk_location_id,
            us_classification,
            regulatory_client_classification,
            adjustment_premium_period_of_credit,
            overseas_broker_address,
            risk_codes,
        } = this.submission;
        this.submissionData = {
            choice_of_law_id: choice_of_law_id ?? undefined,
            interest: interest ?? undefined,
            taxes_payable_by_insured: taxes_payable_by_insured ?? undefined,
            taxes_payable_by_insurer: taxes_payable_by_insurer ?? undefined,
            taxes_payable_by_insurer_and_administered_by_the_insured: taxes_payable_by_insurer_and_administered_by_the_insured ?? undefined,
            taxes_payable_and_administered_by_the_insured: taxes_payable_and_administered_by_the_insured ?? undefined,
            regulatory_risk_location_id: regulatory_risk_location_id ?? undefined,
            us_classification: us_classification ?? undefined,
            regulatory_client_classification: regulatory_client_classification ?? 'Large risks',
            adjustment_premium_period_of_credit: adjustment_premium_period_of_credit ?? undefined,
            overseas_broker_address: overseas_broker_address ?? undefined,
            risk_codes: risk_codes ?? undefined,
        }
    },
    methods: {
        resetEntityClientCodeType() {
            if (this.clientCodeSourceHasClientCode && !this.clientCodeSource.hibl_client_code) {
                this.clientCodeType = 'lux';
            } else if (this.clientCodeSourceHasClientCode && !this.clientCodeSource.lux_client_code) {
                this.clientCodeType = 'hibl';
            } else {
                if (this.isStandalonePolicy) {
                    return;
                }
                this.clientCodeType = this.entity.id === this.submission.manual_firm_order_draft_data?.data.entity_id
                    ? this.submission.manual_firm_order_draft_data.data.client_code_type
                    : null;
            }
        },
        setClientCode() {
            switch (this.clientCodeType) {
                case 'lux':
                    this.clientCode = this.clientCodeSource.lux_client_code;
                    break;
                case 'hibl':
                    this.clientCode = this.clientCodeSource.hibl_client_code;
                    break;
                default:
                    this.clientCode = null;
            }
        },
        syncWithManualFirmOrderDraftData() {
            this.submission.entity_id = this.submission.manual_firm_order_draft_data.data.entity_id;
            this.pricing.net_margin = this.submission.manual_firm_order_draft_data.data.net_margin;
            this.startDate = this.submission.manual_firm_order_draft_data.data.inception_date;
            this.endDate = this.submission.manual_firm_order_draft_data.data.expiry_date;
            this.clientCodeType = this.submission.manual_firm_order_draft_data.data.client_code_type;
        },
        changesMade() {
            this.saveProgressMessage = 'Save progress';
            this.disableDraftDownload = true;
        },
        showHorizontalPriceChange(originalPrice) {
            return this.pricing.structure === 'horizontal' && this.pricing.horizontalPrice > originalPrice;
        },
        setSecurity: function (row, value) {
            // Row-scoped: two rows may belong to the same insurer (one per
            // security), so the target line comes from the edited row itself.
            row.line.security_id = value;

            if (row.line_index === 0) {
                row.offer.security_id = value;
            }

            this.changesMade();
        },
        offerLines(offer) {
            // Non-mutating read for the render path; the selectedOffers
            // watcher seeds offer.lines before any row becomes editable.
            if (offer.lines && offer.lines.length > 0) {
                return offer.lines;
            }

            return [
                {
                    security_id: offer.security_id || null,
                    written_line_percent: offer.written_line_percent,
                    insurer_role: offer.insurer_role || null,
                    manual_subjectivity: offer.manual_subjectivity || null
                }
            ];
        },
        setLineInsurerRole(row, value) {
            row.line.insurer_role = value || null;

            if (row.line_index === 0) {
                row.offer.insurer_role = value || null;
            }

            this.changesMade();
        },
        getOfferLines(offer) {
            if (!offer.lines || offer.lines.length === 0) {
                this.$set(offer, 'lines', this.offerLines(offer));
            }

            return offer.lines;
        },
        lineWrittenAmount(offer, line) {
            const lines = this.offerLines(offer);

            if (lines.length === 1 || !offer.written_line_percent) {
                return offer.written_line_amount;
            }

            return offer.written_line_amount * ((line.written_line_percent || 0) / offer.written_line_percent);
        },
        rowSecurityName(row) {
            return this.lineSecurityName(row.offer, row.line);
        },
        lineSecurityName(offer, line) {
            const security = this.getInsurerSecurities(offer)
                .find(({id}) => id === line.security_id);

            return security ? security.name : 'Not selected';
        },
        findSelectedOffer(offerId) {
            return this.selectedOffers.find(selectedOffer => selectedOffer.id === offerId);
        },
        getSecuritiesFormatted: function (securities) {
            return securities.map(function (security) {
                return {
                    'text': security.name,
                    'value': security.id
                };
            });
        },
        async submitFirmOrderForm() {
            if (!this.validDateRange) {
                return;
            }

            if (!this.$refs.firmOrderForm.validate()) {
                return;
            }

            if (!this.submission?.facility?.do_not_display_sof && await this.$refs.sofConfirmation.open() === false) {
                return;
            }

            if (this.startDateYearForPolicyRef !== this.policyRef.substring(2, 4)) {
                this.$refs.policyRefError.open();
                return;
            }

            if (!this.validateRiskCodeValue()) {
                return;
            }

            this.status = 'confirming';

            const response = await this.saveFirmOrder();
            this.firmOrder.data = this.getFirmOrderData();
            this.status = 'idle';

            if (this.isStandalonePolicy) {
                this.submissionComplete = true;
                this.$emit('submission:complete');
            }
            if (!this.isManualFirmOrder) {
                this.disableDraftDownload = false;
                this.draftFirmOrderId = response.data.id;
            }

            await this.goToStep(this.currentStep + 1);
        },
        async goToStep(step) {
            this.$emit('firm-order:navigate-to', step);
        },
        validateRiskCodeValue() {
            this.errors = [];
            const riskCodesPercentage = this.submissionData.risk_codes.map(riskCode => riskCode.value)
                .reduce((total, percentage) => total + percentage, 0);
            if (riskCodesPercentage !== 100) {
                this.errors.push('Total sum of Allocation to premium coding must equal 100%');
            }
            return this.errors.length === 0;
        },
        async saveFirmOrder() {
            if (this.isStandalonePolicy) {
                let firmOrderDraftData = this.filterDataAndAddInsurerUsersToOffers(this.getFirmOrderData());

                if (this.isConvertedFromManualSubmission) {
                    firmOrderDraftData = this.addFeedbackToOffersIfAvailable(firmOrderDraftData);
                }

                this.$emit('standalonePolicy:manualFirmOrderDraft', this.getFirmOrderData());

                return axios.post(`/api/broker/submission/${this.submission.id}/manual-firm-order/draft`, firmOrderDraftData)
            }

            if (this.isManualFirmOrder) {
                let firmOrderDraftData = this.filterDataAndAddInsurerUsersToOffers(this.getFirmOrderData());

                return axios.post(`/api/broker/submission/${this.submission.id}/manual-firm-order/draft`, firmOrderDraftData)
            }

            return axios.post(`/api/broker/submission/${this.submission.id}/firm-order/draft`, this.getFirmOrderData())
        },
        async saveFirmOrderProgress() {
            this.status = 'saving';

            const response = await this.saveFirmOrder();

            this.saveProgressMessage = 'Saved';
            if (!this.isManualFirmOrder) {
                this.disableDraftDownload = false;
                this.draftFirmOrderId = response.data.id;
            }
            this.status = 'idle';
        },
        async firmOrderPdf() {
            this.status = 'downloading';

            const fileUrl = (await axios.get(`/api/submission/${this.submission.id}/firm-order/download/${this.draftFirmOrderId}`)).data.url;
            window.location.href = fileUrl;

            this.status = 'idle';
        },
        async captiveFirmOrderPdf() {
            this.status = 'captiveDownloading';

            const fileUrl = (await axios.get(`/api/submission/${this.submission.id}/firm-order/captive-download/${this.draftFirmOrderId}`)).data.url;
            window.location.href = fileUrl;

            this.status = 'idle';
        },
        addFeedbackToOffersIfAvailable(firmOrderData) {
            firmOrderData.offers = firmOrderData.offers.map(offer => {
                    offer.feedback = this.submission.manual_submission_tracker.insurers.find(mstIns => mstIns.insurer_company_id === offer.insurer_company_id)?.feedback || null
                    return offer;
                }
            );
            return firmOrderData
        },
        allSubjectivitiesSelected(checkList) {
            const totalItems = checkList.length;
            const selectedItems = checkList.filter(s => s.selected === true).length;

            if (this.isManualFirmOrder) {
                return true;
            }

            return totalItems === selectedItems || 'All subjectivities must be confirmed';
        },
        formattedDate(date) {
            return this.$options.filters.formatDate(date, null, 'YYYY-MM-DD');
        },
        getFirmOrderData() {
            const data = {};

            // One payload row per security line: rows for the same insurer share
            // the offer id but carry their own security and written line split.
            data.offers = [];
            this.offeredOffers.filter(function (offer) {
                return offer.is_selected === true;
            }).forEach(offer => {
                const lines = this.offerLines(offer);

                lines.forEach(line => {
                    const row = {
                        ...offer,
                        security_id: line.security_id,
                        insurer_role: line.insurer_role || null,
                        manual_subjectivity: lines.length > 1 ? (line.manual_subjectivity || null) : (offer.manual_subjectivity || null),
                        written_line_percent: lines.length > 1 ? line.written_line_percent : offer.written_line_percent,
                        written_line_amount: this.lineWrittenAmount(offer, line)
                    };
                    delete row.lines;

                    data.offers.push(row);
                });
            });

            data.inception_date = this.startDate;
            data.expiry_date = this.endDate;
            data.submission_id = this.submission.id;
            data.pricing_structure = this.pricing.structure;
            data.waiting_period_interest_rate = this.waitingPeriodInterestRate;
            data.net_margin = this.pricing.net_margin;
            data.gross_margin = this.pricing.gross_margin;
            data.entity_id = this.submission.entity_id;
            data.policy_reference = this.policyRef;
            data.policy_umr = this.policyUmr;

            if (this.isCaptiveReinsurance) {
                data.booked_location_id = this.bookedLocationId;
                data.insured_transaction = this.insuredTransaction;
                data.special_conditions = this.specialConditions;
                data.obligor_address = this.obligorAddress;
            }

            if (this.isManualFirmOrder) {
                data.insurersUsers = this.selectedInsurersUsersList;
            }

            data.submission_data = this.submissionData;

            data.client_code_type = this.clientCodeType;
            data.client_code = this.clientCode;

            return data;
        },
        filterFirmOrderDataForManualFirmOrderDraft(firmOrderData) {
            return (({ entity_id, expiry_date, inception_date, pricing_structure, offers, policy_reference, policy_umr, client_code_type, client_code, submission_data, net_margin }) => (
                {
                    entity_id,
                    expiry_date,
                    inception_date,
                    pricing_structure,
                    offers,
                    policy_reference,
                    policy_umr,
                    client_code_type,
                    client_code,
                    submission_data,
                    net_margin
                }
            ))(firmOrderData);
        },
        filterOffersForManualFirmOrderDraft(draftData) {
            draftData.offers = draftData.offers.map((offer) => {
                return (({ insurer_company_id, type, brokerage, price, flat_fee, waiting_period, written_line_amount, written_line_percent, manual_subjectivity, percentage_of_margin, minimum_price, security_id, captive_insurer_company_id, insurer_role }) => (
                    {
                        percentage_of_margin,
                        minimum_price,
                        insurer_company_id,
                        type,
                        brokerage,
                        price,
                        flat_fee,
                        waiting_period,
                        written_line_amount,
                        written_line_percent,
                        manual_subjectivity,
                        security_id,
                        captive_insurer_company_id,
                        insurer_role: insurer_role || null,
                    }
                ))(offer);
            });

            return draftData;
        },
        addInsurerUsersToOffers(insurerUsers, draftData) {
            draftData.offers.map((offer) => {
                offer.selectedUsers = insurerUsers[offer.insurer_company_id];
                return offer;
            });

            return draftData;
        },
        getInsurerSecurities(offer) {
            if ((offer.securities || []).length > 0) {
                return offer.securities;
            }

            const facilitySecurities = this.submission?.facility?.securities?.filter(security => security.insurer_company_id === offer.insurer_company_id) || [];
            if (facilitySecurities.length > 0) {
                return facilitySecurities;
            }

            if (this.canEditSecurityLines) {
                const insurerCompanyId = offer.insurer_company_id ?? offer.insurer?.id;

                return this.availableSecurities.filter(security => security.insurer_company_id === insurerCompanyId);
            }

            return [];
        },
        loadAvailableSecurities() {
            if (!this.canEditSecurityLines) {
                return;
            }

            const securityUrl = this.submission.facility_id
                ? `broker/security/list/${this.submission.facility_id}`
                : 'broker/security/list';

            axios.get(this.$root.api(securityUrl)).then(res => {
                this.availableSecurities = res.data.data;
            });
        },
        isRateOffer(type) {
            return type === 'rate';
        },
        isFlatFeeOffer(type) {
            return type === 'flat_fee';
        },
        lineFlatFee(offer, line) {
            const lines = this.offerLines(offer);

            if (lines.length === 1) {
                return offer.flat_fee;
            }

            const totalPercent = lines.reduce((sum, l) => sum + (parseFloat(l.written_line_percent) || 0), 0);

            if (!totalPercent) {
                return 0;
            }

            return (parseFloat(offer.flat_fee) || 0) * ((parseFloat(line.written_line_percent) || 0) / totalPercent);
        },
        isPercentageOfMarginOffer(type) {
            return ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(type);
        },
        isRetainedPercentOffer(type) {
            return type === 'retained_percent';
        },
        calculateMaximumInsuredExposure(waitingPeriod, writtenLineAmount) {
            if (!Number.isNaN(parseFloat(writtenLineAmount))) {
                const wpi = this.calculateWaitingPeriodInterest(writtenLineAmount, waitingPeriod);
                return maximumInsuredExposure.calculate(writtenLineAmount, wpi);
            }
            return 0;
        },
        calculateWaitingPeriodInterest(writtenLineAmount, waitingPeriod) {
            const waitingPeriodInterestRate = parseFloat(this.waitingPeriodInterestRate);
            if (!Number.isNaN(waitingPeriodInterestRate) && !Number.isNaN(parseFloat(writtenLineAmount))) {
                return waitingPeriodInterest.calculate(writtenLineAmount, waitingPeriod, waitingPeriodInterestRate);
            }

            return 0;
        },
        calculateTotalEstimated(offer, includeWPI = true) {
            if (this.isFlatFeeOffer(offer.type)) {
                return offer.flat_fee;
            }

            if (!Number.isNaN(parseFloat(offer.written_line_amount))) {
                const price = this.pricing.structure === 'horizontal' ? this.pricing.horizontalPrice : offer.price;
                const days = (Number.isInteger(this.firmOrderTenor)) ? this.firmOrderTenor : this.submission.total_risk_period;
                const maximumInsuredExposure = this.calculateMaximumInsuredExposure(offer.waiting_period, offer.written_line_amount);
                const value = (includeWPI && this.wpiIsIncludedInEstimatedPremium && maximumInsuredExposure) ? maximumInsuredExposure : offer.written_line_amount;
                if (offer.written_line_percent) {
                    return premium.calculate(value, price, days);
                }
            }

            return 0;
        },
        generateRef() {
            if (!this.clientCodeType || !this.startDateYearForPolicyRef) {
                if (this.policyRef || this.policyUmr) {
                    this.policyRef = null;
                    this.policyUmr = null;
                }

                return;
            }

            const umrPrefix = this.clientCodeType === 'hibl' ? 'B0180' : 'B1703';
            const year = this.startDateYearForPolicyRef;
            const policyRefPrefix = "TX" + year;
            const paddedRiskId = this.submission.id.toString().padStart(5, '0');

            this.policyRef = policyRefPrefix + paddedRiskId;
            this.policyUmr = umrPrefix + this.policyRef;
        },
        filterDataAndAddInsurerUsersToOffers(firmOrderData) {
            return this.addInsurerUsersToOffers(firmOrderData.insurersUsers,
                this.filterOffersForManualFirmOrderDraft(this.filterFirmOrderDataForManualFirmOrderDraft(firmOrderData)));
        },
    }
}
</script>

<style lang="scss" scoped>
.security-line-percent {
    :deep(input[type="number"]) {
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}

.dashed-rows .x-table.x-datatable.theme--dark tbody tr.retained_percent {
    border-bottom: none;
}

tr.retained_percent {
    box-shadow: inset 0 0 0 1px $color-blue;
}
</style>
